import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import MDialog from "components/Shared/MDialog";
import {
  getTabbedHistoryTable,
  formatNoteDateCreated,
  getNoteDetails,
} from "components/Homeowner/History/DataFormatters";
import {
  addCallLog,
  updateCallLog,
  deleteCallLog,
  getCallLog,
  getCallLogSettings,
  getCallLogContacts,
} from "requests/contactEfforts";
import { invalidateDetailedHistory } from "requests/homeowner/caseforms";
import { invalidateNextNotification } from "requests/schedulingAssistant";
import { CASE_CALL_LOG_ADD, CASE_CALL_LOG_DELETE, CASE_CALL_LOG_EDIT } from "typeConstants/rights";
import AddEditCallLogDialog, { CallLogEntryForm } from "components/Homeowner/ContactEfforts/AddEditCallLogDialog";
import { MSpinner } from "components/Shared";
import { CallLog, ContactEffortsSettingsDto, ContactEffortsContactsDto } from "interfaces/Mueller.Business.Models";
import { ColumnDefinition } from "components/Shared/MTable";
import { dateTimeToString, dateToString } from "components/Shared/Formatters";
import _ from "lodash";

interface CallLogsProps {
  /** Control number of the case */
  controlNumber: Number;
  /** Column definitions for the table, if null, standard columns will be used */
  columnDefinitions?: ColumnDefinition[];
  /** Call log data to display in the table */
  tableData: CallLog[];
  /** Callback to get new data after add, edit, close, or delete */
  refreshCallback?: (forceRefresh?: boolean) => void;
  /** If true, the user will be able to add a new call log entry */
  canAdd?: boolean;
  /** If true, the user will be able to edit call log entries */
  canUpdate?: boolean;
  /** If true, the user will be able to delete call log entries */
  canDelete?: boolean;
}

const CallLogs = (props: CallLogsProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddEditDialog, setShowAddEditDialog] = useState(false);
  const [fetchingCallLog, setFetchingCallLog] = useState(false);
  const [callLogData, setCallLogData] = useState<CallLogEntryForm>(null);
  const [callLogSettings, setCallLogSettings] = useState<ContactEffortsSettingsDto>(null);
  const [callLogContacts, setCallLogContacts] = useState<ContactEffortsContactsDto>(null);
  const [showContacts, setShowContacts] = useState(false);
  const idToDelete = useRef(null);
  const mounted = useRef(true);
  const previousCallLog = useRef(null);
  const interval = useRef(null);

  useEffect(() => {
    props.refreshCallback && props.refreshCallback();
    getCallLogSettings(
      props.controlNumber,
      (resp) => {
        setCallLogSettings(resp);
      },
      () => {},
      mounted,
    );
    getCallLogContacts(
      props.controlNumber,
      (resp) => {
        setCallLogContacts(resp);
      },
      () => {},
      mounted,
    );
    return () => {
      mounted.current = false;
      if (interval.current) {
        clearInterval(interval.current);
        interval.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (fetchingCallLog && !interval.current) {
      interval.current = setInterval(() => {
        props.refreshCallback?.(true);
      }, 2000); // Refreshing call log to pick up on events from PNX
    }
  }, [fetchingCallLog]);

  useEffect(() => {
    // Clear refresh interval once we get a new calllog back
    if (!_.isEqual(previousCallLog.current, props.tableData) && interval.current) {
      clearInterval(interval.current);
      interval.current = null;
      // Refresh contacts
      getCallLogContacts(
        props.controlNumber,
        (resp) => {
          setCallLogContacts(resp);
        },
        () => {},
        mounted,
      );
    }
    previousCallLog.current = props.tableData;
  }, [props.tableData]);

  const getEmptyCallLog = () => ({
    contactEffortsId: null,
    controlIdNumber: props.controlNumber,
    phoneNumber: "",
    contactDate: dateToString(new Date()),
    contactTime: new Date(), // This is only used on the front end to support separate date and time controls
    contactPersonTypeCode: null,
    spokeWith: "",
    resultCode: null,
    notes: null,
  });

  const handleData = (data: CallLogEntryForm) => {
    // These ids need to be strings to work with the select control
    return {
      ...data,
      contactPersonTypeCode: `${data.contactPersonTypeCode}`,
      resultCode: `${data.resultCode}`,
      contactTime: new Date(data.contactDate), // TimePicker expects datetime
    };
  };

  const onCallLogSuccess = (data: CallLogEntryForm) => {
    setFetchingCallLog(false);
    let updated = handleData(data);
    setCallLogData(updated);
  };

  const onCallLogError = () => {
    setFetchingCallLog(false);
    if (showDeleteDialog) setShowDeleteDialog(false);
    if (showAddEditDialog) setShowAddEditDialog(false);
  };

  const onEdit = (rowData: CallLog) => {
    setFetchingCallLog(true);
    setCallLogData(null);
    getCallLog(rowData.id, props.controlNumber, onCallLogSuccess, onCallLogError, mounted);
    setShowAddEditDialog(true);
  };

  // const onDelete = (rowData: CallLog) => {
  //   idToDelete.current = rowData.id;
  //   setShowDeleteDialog(true);
  // };

  const onAdd = () => {
    let emptyLog = getEmptyCallLog();
    setCallLogData(emptyLog);
    setShowAddEditDialog(true);
  };

  const onSave = (formData: CallLogEntryForm) => {
    // Defensive: dialog may include UI-only fields; backend contract expects ContactEffortsDto shape.
    const { contactSelection, ...sanitizedFormData } = (formData as any) ?? {};

    // Ids from select control are strings, back end expects ints
    let updated = {
      ...sanitizedFormData,
      contactTimeRangeId: null, // Setting this to null so the back end determines time range based on contact date
    };
    // contactDate is returned as a string and it needs to have time set to match contactTime
    let updatedContactDate = new Date(updated.contactDate);
    updatedContactDate.setHours(updated.contactTime.getHours(), updated.contactTime.getMinutes());
    // Convert date to string without timezone for backend
    updated.contactDate = dateTimeToString(updatedContactDate);
    setFetchingCallLog(true);
    if (updated.contactEffortsId) {
      updateCallLog(updated, addUpdateCallback, onCallLogError, mounted);
    } else {
      addCallLog(updated, addUpdateCallback, onCallLogError, mounted);
    }
  };

  const addUpdateCallback = () => {
    invalidateDetailedHistory(props.controlNumber);
    invalidateNextNotification(props.controlNumber);
    props.refreshCallback?.(true);
    setShowAddEditDialog(false);
    setFetchingCallLog(false);
  };

  const deleteEntry = () => {
    deleteCallLog(idToDelete.current, props.controlNumber, deleteCallback, onCallLogError, mounted);
  };

  const deleteCallback = () => {
    invalidateDetailedHistory(props.controlNumber);
    invalidateNextNotification(props.controlNumber);
    props.refreshCallback?.(true);
    setShowDeleteDialog(false);
  };

  const hideAddEdit = () => setShowAddEditDialog(false);
  const hideDelete = () => setShowDeleteDialog(false);

  const getEditButton = (rowData: CallLog) => {
    return rowData.editable ? (
      <IconButton size="small" onClick={() => onEdit(rowData)}>
        <Icon>edit</Icon>
      </IconButton>
    ) : null;
  };

  {
    /* No deletes supported at the moment per https://mueller-inc.atlassian.net/browse/SD-22470 */
  }
  // const getDeleteButton = (rowData: CallLog) => {
  //   return (
  //     <div>
  //       <i className="md-icon material-icons" onClick={() => onDelete(rowData)}>delete_forever</i>
  //     </div>
  //   );
  // };

  const deleteDialogActions = [
    <Button key={"call-log-cancel-delete"} onClick={hideDelete}>
      No, go back
    </Button>,
    <Button key={"call-log-confirm-delete"} onClick={deleteEntry}>
      Yes, delete
    </Button>,
  ];

  const standardColumnDefinitions = [
    { name: "Date", dataSource: formatNoteDateCreated, style: { width: "175px" } },
    { name: "Contact", dataSource: "contact", style: { width: "250px" } },
    { name: "Details", dataSource: getNoteDetails },
    { name: "Notes", dataSource: "notes" },
    { name: "Performed By", dataSource: "performedBy" },
  ];

  const getColumnDefinitions = () => {
    let columnDefs = props.columnDefinitions ? [...props.columnDefinitions] : standardColumnDefinitions;
    if (props.canUpdate) columnDefs.push({ name: "Edit", dataSource: getEditButton });
    //if (props.canDelete) columnDefs.push({ name: "Delete", dataSource: getDeleteButton });
    return columnDefs;
  };

  const formatContactValueForDisplay = (rawValue: string) => {
    if (!rawValue) return rawValue;
    // Emails should be displayed as-is
    if (`${rawValue}`.includes("@")) return rawValue;

    const digits = `${rawValue}`.replace(/\D/g, "");
    if (digits.length < 10) return rawValue;

    // Handle values like +1########## by using last 10 digits
    const last10 = digits.slice(-10);
    return `${last10.slice(0, 3)}-${last10.slice(3, 6)}-${last10.slice(6, 10)}`;
  };

  return (
    <>
      <MDialog
        id="delete-call-log-dialog"
        className="delete-call-log-dialog"
        title="Confirm Delete"
        visible={showDeleteDialog}
        focusOnMount={false}
        contentActions={deleteDialogActions}
      >
        <p>Are you sure you want to delete this entry?</p>
        <p>This action cannot be undone.</p>
      </MDialog>
      <MDialog
        className="add-edit-call-log-dialog mgridform-flat"
        title={`${callLogData?.contactEffortsId ? "Edit" : "Add"} Call Log`}
        visible={showAddEditDialog}
        focusOnMount={false}
        width={550}
        sxPaper={{ width: "550px" }}
        hideBackdrop
        disableScrollLock
      >
        {fetchingCallLog ? (
          <MSpinner />
        ) : (
          <AddEditCallLogDialog
            data={callLogData}
            onCancel={hideAddEdit}
            onSave={onSave}
            settings={callLogSettings}
            contacts={callLogContacts}
          />
        )}
      </MDialog>
      <MDialog id="call-log-contacts-dialog" title={"Contacts"} visible={showContacts} focusOnMount={false} width={550}>
        <>
          <ul>
            {callLogContacts?.contacts.map((s) => (
              <li key={`${s.type ?? ""}-${s.name ?? ""}-${s.value ?? ""}`}>
                {`[${s.type}] `}
                {s.name}: {formatContactValueForDisplay(s.value)}
                {!s.isValid ? " (Invalid)" : ""}
              </li>
            ))}
          </ul>
          <Button key={"call-log-hide-contacts"} onClick={() => setShowContacts(!showContacts)}>
            Close
          </Button>
        </>
      </MDialog>
      <div className="call-log-buttons">
        {props.canAdd && (
          <div className="call-log-button">
            <Button className="history-action-button" onClick={onAdd}>
              Add
            </Button>
            <Button
              className="history-action-button"
              key={"call-log-show-contacts"}
              onClick={() => setShowContacts(!showContacts)}
              disabled={!callLogContacts?.contacts.length}
            >
              Contacts
            </Button>
          </div>
        )}
      </div>
      {getTabbedHistoryTable(getColumnDefinitions(), props.tableData, "Call Logs", "id")}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    canAdd: state.user.profile.rights.includes(CASE_CALL_LOG_ADD),
    canUpdate: state.user.profile.rights.includes(CASE_CALL_LOG_DELETE),
    canDelete: state.user.profile.rights.includes(CASE_CALL_LOG_EDIT),
  };
};

export default connect(mapStateToProps)(CallLogs);
